import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { DealBookingType } from '@src/types/OTC';
import { ButtonVariants, ControlPrefix, FormGroup, HStack, SearchSelect, ToggleGroup } from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import { useDealProductBuilder } from './DealProductBuilderProvider/useDealProductBuilder';
import {
  bookingTypeToSelectorType,
  getLoanRateType,
  getOtcDerivativeKind,
  LOAN_RATE_OPTIONS,
  OTC_DERIVATIVE_OPTIONS,
  PRODUCT_SELECTOR_OPTIONS,
  selectorTypeToBookingType,
  type LoanRateType,
  type OtcDerivativeKind,
} from './productConfig';

interface DealProductBuilderHeaderSelectorProps {
  panelID: string;
  onBookingTypeChange?: (bookingType: DealBookingType) => void;
}

export const DealProductBuilderHeaderSelector = memo(
  ({ panelID: _panelID, onBookingTypeChange }: DealProductBuilderHeaderSelectorProps) => {
    const {
      bookingType,
      dispatch,
      actions: { setBookingType: setBookingTypeAction },
    } = useDealProductBuilder();

    const { enableRepoProduct } = useFeatureFlag();

    const selectorType = bookingTypeToSelectorType(bookingType);
    const isLoanSelected = selectorType === 'loan';
    const isOtcSelected = selectorType === 'otc';
    const loanRateType = getLoanRateType(bookingType);
    const otcDerivativeKind = getOtcDerivativeKind(bookingType);

    const productSelectorOptions = useMemo(
      () => PRODUCT_SELECTOR_OPTIONS.filter(o => o.value !== 'repo' || enableRepoProduct),
      [enableRepoProduct]
    );

    const selectedOption = useMemo(
      () => productSelectorOptions.find(o => o.value === selectorType),
      [productSelectorOptions, selectorType]
    );

    const handleProductChange = useCallback(
      (opt: (typeof productSelectorOptions)[number] | undefined) => {
        const newSelectorType = opt?.value;
        if (newSelectorType) {
          const newBookingType = selectorTypeToBookingType(newSelectorType, {
            loanRateType: newSelectorType === 'loan' ? loanRateType : undefined,
            otcDerivativeKind: newSelectorType === 'otc' ? getOtcDerivativeKind(bookingType) : undefined,
          });
          dispatch(setBookingTypeAction(newBookingType));
          onBookingTypeChange?.(newBookingType);
        }
      },
      [bookingType, dispatch, loanRateType, onBookingTypeChange, setBookingTypeAction]
    );

    const handleLoanRateChange = useCallback(
      (newRateType: LoanRateType) => {
        const newBookingType = selectorTypeToBookingType('loan', { loanRateType: newRateType });
        dispatch(setBookingTypeAction(newBookingType));
        onBookingTypeChange?.(newBookingType);
      },
      [dispatch, onBookingTypeChange, setBookingTypeAction]
    );

    const handleOtcDerivativeChange = useCallback(
      (kind: OtcDerivativeKind) => {
        const newBookingType = selectorTypeToBookingType('otc', { otcDerivativeKind: kind });
        dispatch(setBookingTypeAction(newBookingType));
        onBookingTypeChange?.(newBookingType);
      },
      [dispatch, onBookingTypeChange, setBookingTypeAction]
    );

    return (
      <HStack justifyContent="space-between" h={48} flex="0 0 auto">
        <HStack alignItems="center" gap="spacing8">
          <FormGroup mb={0}>
            <SearchSelect
              options={productSelectorOptions}
              getLabel={o => o.label}
              prefix={<ControlPrefix>Product</ControlPrefix>}
              selection={selectedOption}
              dropdownWidth={160}
              onChange={handleProductChange}
              data-testid="deal-product-builder-product-selector"
              initialSortByLabel={false}
            />
          </FormGroup>
          {isOtcSelected && (
            <ToggleGroup
              options={OTC_DERIVATIVE_OPTIONS}
              defaultSelectedVariant={ButtonVariants.Priority}
              value={otcDerivativeKind}
              onChange={handleOtcDerivativeChange}
              data-testid="deal-product-builder-otc-derivative-toggle"
            />
          )}
          {isLoanSelected && (
            <ToggleGroup
              options={LOAN_RATE_OPTIONS}
              defaultSelectedVariant={ButtonVariants.Priority}
              value={loanRateType}
              onChange={handleLoanRateChange}
              data-testid="deal-product-builder-loan-rate-toggle"
            />
          )}
        </HStack>
      </HStack>
    );
  }
);
